import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [tokenFamily, setTokenFamily] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load stored auth data on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const storedAccessToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedTokenFamily = localStorage.getItem('tokenFamily');
      const storedUserData = localStorage.getItem('userData');
      
      if (storedAccessToken && storedUserData) {
        try {
          const userData = JSON.parse(storedUserData);
          
          // Set the tokens first
          setAccessToken(storedAccessToken);
          setRefreshToken(storedRefreshToken);
          setTokenFamily(storedTokenFamily);
          setUser(userData);
          
          // Restore authentication state - let the API calls handle token validation
          setIsAuthenticated(true);
          console.log('🔐 Authentication restored from localStorage - tokens will be validated on first API call');
        } catch (error) {
          console.error('Failed to parse stored user data:', error);
          // Clear everything if parsing fails
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('tokenFamily');
          localStorage.removeItem('userData');
          setAccessToken(null);
          setRefreshToken(null);
          setTokenFamily(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };

    initializeAuth();
  }, []);

  // Function to refresh the access token
  const refreshAccessToken = useCallback(async () => {
    if (isRefreshing || !refreshToken) {
      return false;
    }

    try {
      setIsRefreshing(true);
      const response = await authAPI.refreshToken();
      
      // Update tokens
      setAccessToken(response.accessToken);
      setRefreshToken(response.refreshToken);
      setTokenFamily(response.tokenFamily);
      
      // Update localStorage
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('tokenFamily', response.tokenFamily);
      
      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // If refresh fails, logout the user
      logout();
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshToken, isRefreshing]);

  // Function to handle login with new JWT structure
  const login = useCallback((authResponse, userData = null) => {
    let accessToken, refreshToken, tokenFamily, user, subscriptionType, subscription;
    
    // Handle both calling patterns:
    // 1) login(authResponse)
    // 2) login(token, user)
    if (typeof authResponse === 'string') {
      // Pattern 2: login(token, user)
      accessToken = authResponse;
      user = userData;
      // Optional fields may be embedded in userData
      refreshToken = user?.refreshToken || user?.refresh_token || null;
      tokenFamily = user?.tokenFamily || user?.token_family || null;
      subscriptionType = user?.subscriptionType || null;
      subscription = user?.subscription || null;
    } else {
      // Pattern 1: login(authResponse). Normalize common field names
      accessToken = authResponse.accessToken || authResponse.token || authResponse.jwt || null;
      refreshToken = authResponse.refreshToken || authResponse.refresh_token || null;
      tokenFamily = authResponse.tokenFamily || authResponse.token_family || null;
      user = authResponse.user || authResponse.profile || authResponse.account || null;
      subscriptionType = authResponse.subscriptionType || null;
      subscription = authResponse.subscription || null;
    }
    
    if (!accessToken) {
      console.error('Login failed: No access token provided in response');
      return;
    }
    
    // Persist tokens when present
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    if (tokenFamily) localStorage.setItem('tokenFamily', tokenFamily);
    
    if (user) {
      const userWithSubscription = {
        ...user,
        ...(subscriptionType && { subscriptionType }),
        ...(subscription && { subscription })
      };
      localStorage.setItem('userData', JSON.stringify(userWithSubscription));
      setUser(userWithSubscription);
    }
    
    setAccessToken(accessToken);
    setRefreshToken(refreshToken || null);
    setTokenFamily(tokenFamily || null);
    setIsAuthenticated(true);
    
    console.log('🔐 Login successful:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasUser: !!user,
      isAuthenticated: true
    });
  }, []);

  const register = (userData) => {
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = useCallback(async () => {
    try {
      // Call logout API if we have tokens
      if (accessToken || refreshToken) {
        await authAPI.logout();
      }
    } catch (error) {
      console.warn('Logout API call failed, but continuing with local cleanup:', error);
    } finally {
      // Always clean up local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenFamily');
      localStorage.removeItem('userData');
      
      // Reset state
      setAccessToken(null);
      setRefreshToken(null);
      setTokenFamily(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, [accessToken, refreshToken]);

  const value = {
    user,
    accessToken,
    refreshToken,
    tokenFamily,
    isAuthenticated,
    isRefreshing,
    login,
    register,
    logout,
    setUser,
    refreshAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
